import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  FiSearch,
  FiCheckCircle,
  FiXCircle,
  FiEye,
  FiFileText,
  FiClock,
} from "react-icons/fi";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";

export default function ApprovalWorkflow() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Static data - replace with API calls later
  const [approvals] = useState([
    {
      id: 1,
      contentId: "CNT-001",
      title: "Market Outlook Q1 2024",
      type: "Call",
      author: "Analyst-001",
      submittedDate: "2024-01-18",
      changes: "Updated market analysis section",
      status: "Pending",
    },
    {
      id: 2,
      contentId: "CNT-002",
      title: "Sector Analysis: Banking",
      type: "Report",
      author: "Analyst-002",
      submittedDate: "2024-01-15",
      changes: "Added new charts and data",
      status: "Pending",
    },
    {
      id: 3,
      contentId: "CNT-003",
      title: "Weekly Market Update",
      type: "Call",
      author: "Analyst-001",
      submittedDate: "2024-01-20",
      changes: "Minor text corrections",
      status: "Approved",
    },
  ]);

  const handleApprove = (contentId) => {
    Swal.fire({
      title: "Approve Content?",
      text: "This content will be published.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve",
      confirmButtonColor: "#10b981",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire("Approved!", "Content has been approved and published.", "success");
      }
    });
  };

  const handleReject = (contentId) => {
    Swal.fire({
      title: "Reject Content",
      html: `
        <input id="rejectionReason" class="swal2-input" placeholder="Enter rejection reason..." required>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Reject",
      confirmButtonColor: "#ef4444",
      preConfirm: () => {
        const reason = document.getElementById("rejectionReason").value;
        if (!reason) {
          Swal.showValidationMessage("Please enter a rejection reason");
        }
        return reason;
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire("Rejected!", `Content rejected. Reason: ${result.value}`, "info");
      }
    });
  };

  const handleViewDiff = (contentId) => {
    Swal.fire({
      title: "Content Changes",
      html: `
        <div style="text-align: left; padding: 20px;">
          <p><strong>Changes made:</strong></p>
          <ul>
            <li>Updated market analysis section</li>
            <li>Added new data points</li>
            <li>Revised conclusion</li>
          </ul>
        </div>
      `,
      icon: "info",
      confirmButtonColor: "#006e82",
    });
  };

  const filteredApprovals = approvals.filter((approval) => {
    const matchesSearch =
      approval.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      approval.contentId.includes(searchTerm);
    const matchesStatus = statusFilter === "all" || approval.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const indexOfLastItem = (currentPage + 1) * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredApprovals.slice(indexOfFirstItem, indexOfLastItem);
  const pageCount = Math.ceil(filteredApprovals.length / itemsPerPage);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  return (
    <>
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content">
            {/* Breadcrumb and Header Section with Card Shadow */}
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                padding: "20px",
              }}
            >
                   <div className="content-page-header content-page-headersplit">
                <h5>Approval Workflow</h5>
              </div>
              <nav
                aria-label="breadcrumb"
                style={{ "--bs-breadcrumb-divider": "none", marginBottom: "15px" }}
              >
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <NavLink to="/dashboard">Dashboard</NavLink> /
                  </li>
                  <li className="breadcrumb-item">
                    <NavLink to="/admin/research/approval-workflow">Research</NavLink> /
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Approval Workflow
                  </li>
                </ol>
              </nav>
         
            </div>

            {/* Filters Section with Card Shadow */}
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                padding: "20px",
              }}
            >
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <div className="position-relative">
                    <FiSearch
                      style={{
                        position: "absolute",
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#64748b",
                        zIndex: 1,
                      }}
                    />
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by title or content ID..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(0);
                      }}
                      style={{ paddingLeft: "40px" }}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-3">
                  <select
                    className="form-control"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(0);
                    }}
                  >
                    <option value="all">All Status</option>
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Approvals Table Section with Card Shadow */}
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                padding: "20px",
              }}
            >
              <div className="row">
                <div className="col-12">
                  <div className="table-responsive">
                    <table className="table datatable">
                      <thead>
                        <tr>
                          <th>Content ID</th>
                          <th>Title</th>
                          <th>Type</th>
                          <th>Author</th>
                          <th>Submitted</th>
                          <th>Changes</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.length === 0 ? (
                          <tr>
                            <td colSpan="8">No data available</td>
                          </tr>
                        ) : (
                          currentItems.map((approval) => (
                            <tr key={approval.id}>
                              <td style={{ fontWeight: "500" }}>{approval.contentId}</td>
                              <td style={{ fontWeight: "500" }}>{approval.title}</td>
                              <td>
                                <span className="badge" style={{
                                  background: "#f0f9ff",
                                  color: "#006e82",
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                }}>
                                  {approval.type}
                                </span>
                              </td>
                              <td>{approval.author}</td>
                              <td>{approval.submittedDate}</td>
                              <td>{approval.changes}</td>
                              <td>
                                <span className={approval.status === "Approved" ? "badge-active" : approval.status === "Rejected" ? "badge-pending" : "badge-pending"}>
                                  {approval.status}
                                </span>
                              </td>
                              <td>
                                <div className="table-actions d-flex justify-content-center">
                                  <div
                                    className="delete-table me-2"
                                    onClick={() => handleViewDiff(approval.contentId)}
                                    title="View Changes"
                                    style={{ cursor: "pointer" }}
                                  >
                                    <i className="fas fa-eye"></i>
                                  </div>
                                  {approval.status === "Pending" && (
                                    <>
                                      <div
                                        className="delete-table me-2"
                                        onClick={() => handleApprove(approval.contentId)}
                                        title="Approve"
                                        style={{ cursor: "pointer", color: "#10b981" }}
                                      >
                                        <i className="fas fa-check-circle"></i>
                                      </div>
                                      <div
                                        className="delete-table"
                                        onClick={() => handleReject(approval.contentId)}
                                        title="Reject"
                                        style={{ cursor: "pointer", color: "#ef4444" }}
                                      >
                                        <i className="fas fa-times-circle"></i>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Pagination */}
              {pageCount > 1 && (
                <nav className="mt-4 pagination justify-content-end gap-2">
                  <select
                    className="form-select"
                    style={{ width: "70px", padding: "0 13px" }}
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="30">30</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <ReactPaginate
                    className="pagination justify-content-center"
                    previousLabel={"Previous"}
                    nextLabel={"Next"}
                    pageCount={pageCount}
                    pageClassName={"page-link"}
                    onPageChange={handlePageClick}
                    containerClassName={"pagination"}
                    previousLinkClassName={"page-link"}
                    nextLinkClassName={"page-link"}
                    disabledClassName={"disabled"}
                    activeClassName={"active"}
                  />
                </nav>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
